import { Box, Button, IconButton, Tooltip, Typography } from '@mui/material';
import UndoIcon from '@mui/icons-material/Undo';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import { useLiveMatch } from '../LiveMatchContext.tsx';
import { TeamSide } from '../liveMatchTypes.ts';

const MAX_TIMEOUTS = 2;

// 2.32: 8 sostituzioni per set (circolare integrazione regolamento 01/11/2026)
const MAX_SUBSTITUTIONS = 8;

interface Props {
  side: TeamSide;
  isGlobalSide: boolean;   // true per un solo lato: mostra i pulsanti globali (Undo, Swap)
  onTimeout: () => void;
  onSwap: () => void;
  maxSubsReached: boolean;
}

export default function ActionPanel({ side, isGlobalSide, onTimeout, onSwap, maxSubsReached }: Props) {
  const { state, dispatch } = useLiveMatch();

  const teamName   = side === 'HOME' ? state.homeTeamName   : state.awayTeamName;
  const shirtColor = side === 'HOME' ? state.homeShirtColor : state.awayShirtColor;
  const usedTimeouts = side === 'HOME' ? state.homeTimeouts : state.awayTimeouts;
  const remainingTimeouts = MAX_TIMEOUTS - usedTimeouts;
  const timeoutDisabled   = remainingTimeouts === 0;

  const substitutionCount = state.eventLog.filter(
    (e) =>
      e.type === (side === 'HOME' ? 'SUBSTITUTION_HOME' : 'SUBSTITUTION_AWAY') &&
      e.setNumber === state.currentSet,
  ).length;

  const canUndo = state.eventLog.length > 0;

  function handleTimeout() {
    if (timeoutDisabled) return;
    dispatch({ type: 'TIMEOUT', team: side });
    onTimeout();
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        height: '100%',
        p: { xs: 0.75, sm: 1 },
        gap: 1,
      }}
    >
      {/* Team header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexShrink: 0 }}>
        <Box
          sx={{
            width: 14, height: 14,
            borderRadius: '50%',
            bgcolor: shirtColor,
            border: '1.5px solid rgba(255,255,255,0.3)',
            flexShrink: 0,
          }}
        />
        <Typography
          noWrap
          sx={{ fontWeight: 700, fontSize: { xs: '0.65rem', sm: '0.75rem' }, color: 'rgba(255,255,255,0.8)' }}
        >
          {teamName}
        </Typography>
      </Box>

      {/* +1 PUNTO — pulsante principale */}
      <Button
        variant="contained"
        color="success"
        onClick={() => dispatch({ type: 'ADD_POINT', scoringTeam: side })}
        sx={{
          flexShrink: 0,
          fontWeight: 900,
          fontSize: { xs: '0.85rem', sm: '1rem', md: '1.1rem' },
          py: { xs: 1.5, sm: 2 },
          minHeight: { xs: 52, sm: 64 },
          borderRadius: 2,
          boxShadow: '0 4px 12px rgba(76,175,80,0.4)',
          '&:hover': { boxShadow: '0 6px 16px rgba(76,175,80,0.6)' },
        }}
      >
        +1 PUNTO
      </Button>

      {/* Timeout */}
      <Box sx={{ flexShrink: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
          <Typography sx={{ fontSize: 10, color: 'rgba(255,255,255,0.5)' }}>Timeout</Typography>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {Array.from({ length: MAX_TIMEOUTS }, (_, i) => (
              <Box
                key={i}
                sx={{
                  width: 10, height: 10,
                  borderRadius: '50%',
                  bgcolor: i < remainingTimeouts ? 'warning.main' : 'rgba(255,255,255,0.15)',
                  border: '1px solid',
                  borderColor: i < remainingTimeouts ? 'warning.light' : 'rgba(255,255,255,0.2)',
                }}
              />
            ))}
          </Box>
        </Box>
        <Tooltip title={timeoutDisabled ? 'Timeout esauriti' : ''}>
          <span>
            <Button
              variant="outlined"
              onClick={handleTimeout}
              disabled={timeoutDisabled}
              sx={{
                width: '100%',
                py: 0.75,
                fontSize: { xs: '0.7rem', sm: '0.8rem' },
                fontWeight: 700,
                color: timeoutDisabled ? 'rgba(255,255,255,0.3)' : 'warning.main',
                borderColor: timeoutDisabled ? 'rgba(255,255,255,0.15)' : 'warning.main',
                '&:hover': { borderColor: 'warning.light', bgcolor: 'rgba(255,152,0,0.08)' },
                '&.Mui-disabled': { color: 'rgba(255,255,255,0.2)', borderColor: 'rgba(255,255,255,0.12)' },
              }}
            >
              TIMEOUT
            </Button>
          </span>
        </Tooltip>
      </Box>

      {/* Sostituzioni */}
      <Typography
        sx={{
          fontSize: 10,
          flexShrink: 0,
          color: maxSubsReached ? 'error.main' : 'rgba(255,255,255,0.45)',
          fontWeight: maxSubsReached ? 700 : 400,
        }}
      >
        Sostituzioni: {substitutionCount} / {MAX_SUBSTITUTIONS}
        {maxSubsReached && ' ⚠'}
      </Typography>

      {/* Spacer */}
      <Box sx={{ flex: 1 }} />

      {/* Pulsanti globali — solo sul pannello isGlobalSide */}
      {isGlobalSide && (
        <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
          <Tooltip title="Annulla ultima azione">
            <span style={{ flex: 1 }}>
              <IconButton
                onClick={() => dispatch({ type: 'UNDO_LAST_EVENT' })}
                disabled={!canUndo}
                sx={{
                  width: '100%',
                  borderRadius: 1,
                  border: '1px solid rgba(255,255,255,0.2)',
                  color: canUndo ? 'rgba(255,255,255,0.7)' : 'rgba(255,255,255,0.2)',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.08)' },
                  '&.Mui-disabled': { color: 'rgba(255,255,255,0.15)' },
                }}
              >
                <UndoIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Scambia lati squadre">
            <IconButton
              onClick={onSwap}
              sx={{
                flex: 1,
                borderRadius: 1,
                border: '1px solid rgba(255,255,255,0.2)',
                color: 'rgba(255,255,255,0.7)',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.08)' },
              }}
            >
              <SwapHorizIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      )}
    </Box>
  );
}
